import { ReactNode } from "react";
import { Collapse, Flex, Spin, Alert, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import { WhiteBoxBlock } from "ui";
import { CardSteps } from "components";
import { routes } from "constants/routes";
import type { ContractHistoryRow } from "services/contractService";
import { dateFormat } from "utils";
import { useStores } from "stores";
import { ReactComponent as ArrowUp } from "images/table/Arrow-up.svg";
import { ReactComponent as ArrowDown } from "images/table/Arrow-down.svg";
import styles from "./contractSteps.module.css";

function hasCommentsInDetails(rows: ContractHistoryRow[]): boolean {
  return rows.some((row) => Boolean(row.comment?.trim()));
}

type DetailLinesProps = {
  rows: ContractHistoryRow[];
  showInitiator: boolean;
};

const ContractStatusDetailLines = ({ rows, showInitiator }: DetailLinesProps) => (
  <Flex vertical gap={4} className={styles.subStatusList}>
    {rows.map((row) => (
      <div key={row.id} className={styles.subStatusEntry}>
        <Typography.Text type="secondary" className={styles.subStatusEntryLine}>
          <span>{dateFormat(row.statusDate, true)}</span>
          {row.userName ? (
            showInitiator && row.userId ? (
              <Link to={`/${routes.users}/${row.userId}`} className={styles.subStatusEntryUser}>
                ({row.userName})
              </Link>
            ) : (
              <span className={styles.subStatusEntryUser}> ({row.userName})</span>
            )
          ) : null}
        </Typography.Text>
        {row.comment ? (
          <Flex vertical>
            <Typography.Text type="secondary" className={styles.subStatusEntryComment}>
              {row.comment}
            </Typography.Text>
          </Flex>
        ) : null}
      </div>
    ))}
  </Flex>
);

type PastStepCollapseProps = {
  rows: ContractHistoryRow[];
  showInitiator: boolean;
  defaultOpen?: boolean;
};

const ContractHistoryCollapse = ({ rows, showInitiator, defaultOpen = false }: PastStepCollapseProps) => (
  <Collapse
    ghost
    bordered={false}
    size="small"
    expandIconPosition="end"
    className={styles.subStatusCollapse}
    defaultActiveKey={defaultOpen ? ["detail"] : []}
    expandIcon={({ isActive }) => (
      <span className={styles.collapseArrowWrap}>
        {isActive ? <ArrowUp className={styles.collapseArrowUp} /> : <ArrowDown className={styles.collapseArrowDown} />}
      </span>
    )}
    items={[
      {
        key: "detail",
        label: <span className={styles.collapsePanelLabel}>Подробнее ({rows.length})</span>,
        children: <ContractStatusDetailLines rows={rows} showInitiator={showInitiator} />,
      },
    ]}
  />
);

export const ContractSteps = observer(() => {
  const { contractCard, user } = useStores();

  const { isRiskManager, isSeniorOperator, isJuniorOperator } = user.roles;
  const showInitiator = isRiskManager || isSeniorOperator || isJuniorOperator;

  const { items: stepItems, currentIndex, currentStatus } = contractCard.history.getStepsViewModel();

  const items = stepItems.map((step, index) => {
    const isCurrentStep = index === currentIndex;
    let description: ReactNode | undefined;
    if (step.detailRows.length > 0) {
      const useCollapse = hasCommentsInDetails(step.detailRows);
      const detailBlock = <ContractStatusDetailLines rows={step.detailRows} showInitiator={showInitiator} />;
      if (useCollapse) {
        description = (
          <ContractHistoryCollapse rows={step.detailRows} showInitiator={showInitiator} defaultOpen={isCurrentStep} />
        );
      } else {
        description = <div className={styles.subStatusBlock}>{detailBlock}</div>;
      }
    }

    return {
      rawStatus: step.rawStatus,
      title: step.title,
      description,
    };
  });

  return (
    <Spin spinning={contractCard.isPending || contractCard.history.loading}>
      {contractCard.history.fetchError && (
        <WhiteBoxBlock>
          <Flex vertical gap="small">
            <Typography.Text>Не удалось загрузить историю статусов договора</Typography.Text>
            <Alert showIcon type="error" message={contractCard.history.fetchError.title} />
          </Flex>
        </WhiteBoxBlock>
      )}
      {items.length > 0 && <CardSteps items={items} currentStatus={currentStatus} currentIndex={currentIndex} />}
    </Spin>
  );
});

export const ContractStepsContainer: React.FC<{
  children: ReactNode;
}> = observer(({ children }) => {
  return (
    <Flex gap="large">
      <Flex className={styles.statusHistoryBlock} id="contract-steps">
        <ContractSteps />
      </Flex>
      <Flex vertical gap="large" className="flex-grow overflow-auto">
        {children}
      </Flex>
    </Flex>
  );
});
